/* eslint-disable react/jsx-key */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HeadersFunction, LoaderFunction, LoaderFunctionArgs, MetaFunction, useLoaderData } from "react-router";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import ArticleComponent from "~/components/articleCards/ArticleComponent";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import Chip from "~/components/shared-components/Chip";
import ClientOnly from "~/components/shared-components/ClentOnly";
import LoadingSpinner from "~/components/shared-components/LoadingSpinner";
import NodataFound from "~/components/shared-components/NoDataFound";
import NoHasMoreText from "~/components/shared-components/NoHasMoreText";
import useDisplayStickyButton from "~/hook/useDisplayStickyButton";
import { ArticleType, categoryAndAutherProps } from "~/interface/article/article";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import API_PATH from "~/utils/api-routes";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import api from "~/utils/Interceptor";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

interface NewsProps {
  news: ArticleType[];
  title: string;
  newsCategory: categoryAndAutherProps[];
  author: categoryAndAutherProps[];
  lang: string;
  totalNewsCount: number;
  availableYears: number[];
  newsGroupByData: {
    [key: string]: ArticleType[];
  };
  years: number[];
}

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";
  const meta = getPageMeta("news", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader: LoaderFunction = async ({ params, request }: LoaderFunctionArgs) => {
  const lang = params.lang;

  //fetch auther anme array from master data
  const apiService = new ApiService(lang || LANG.english);
  const authorResponse = await apiService.getMasterData();
  const author = authorResponse["blog_author"];
  const newsCategory = authorResponse["news_category"];

  //get min and max year from api
  const yearsArray = await api.get(API_PATH.NEWS_YEAR);

  if (yearsArray?.data?.status !== 200) {
    throw new Response("Data not found", { status: 404 });
  }

  const years = yearsArray?.data?.status === 200 ? yearsArray?.data?.data : [];

  //fetch news data
  const responce = await apiService.getNewsData({
    pageIndex: 1,
    pageSize: 12,
  });

  const news = responce?.rows || [];

  const totalNewsCount = responce?.count || 0;
  const title = lang === LANG.gujarati ? "સમાચાર" : "News";

  const newsGroupByData = news.reduce((acc: any, obj: ArticleType) => {
    const year = obj.year;
    if (typeof year !== "undefined" && year !== null) {
      const key = String(year);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
    }
    return acc;
  }, {});

  const availableYears = Object.keys(newsGroupByData)
    .map((key) => parseInt(key))
    .sort((a, b) => b - a);

  const DATA = {
    news,
    title,
    newsCategory,
    author,
    lang,
    totalNewsCount,
    availableYears,
    newsGroupByData,
    years,
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("if-none-match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.NEWS.HEADERS, ETag: etag },
  });
};

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
  breadcrumbSchema: (match: any) => [
    { name: "Home", path: "/" },
    { name: match.data?.title || "News", path: "/news" },
  ],
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

// export function headers({ loaderHeaders }: { loaderHeaders: Headers }) {
//   return {
//     "Cache-Control": loaderHeaders.get("Cache-Control") ?? cacheHeaders.none,
//     ETag: cacheHeaders.version,
//   };
// }

function News() {
  const { news, author, lang, totalNewsCount, availableYears, newsGroupByData, years } = useLoaderData<NewsProps>();
  const { showTopButton } = useDisplayStickyButton();
  const { t } = useTranslation();
  const virtuoso = React.useRef<VirtuosoHandle | null>(null);
  const yearRefs = useRef<Record<number, HTMLElement | null>>({});
  const ticking = useRef(false);
  const apiservice = new ApiService(lang || LANG.english);

  const [pageIndex, setPageIndex] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(years[0]);
  const [availableYearsArray, setAvailableYearsArray] = useState<number[]>(availableYears);
  const [newsGroupByYearData, setNewsGroupByYearData] = useState<{
    [key: string]: ArticleType[];
  }>(newsGroupByData);

  const handleFetchNextPageBlogData = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const response = await apiservice.getNewsData({
        pageIndex: pageIndex + 1,
        pageSize: 12,
      });
      const newNewsData = response?.rows;

      if (newNewsData && newNewsData.length > 0) {
        const newNewsGroupByData = newNewsData.reduce((acc: any, obj: ArticleType) => {
          const year = obj.year;
          if (typeof year !== "undefined" && year !== null) {
            if (!acc[year]) {
              acc[year] = [];
            }
            acc[year].push(obj);
          }
          return acc;
        }, {});
        const newAvailableYears = Object.keys(newNewsGroupByData)
          .map((key) => parseInt(key))
          .sort((a, b) => b - a);
        setAvailableYearsArray((prevYears) => {
          const uniqueYears = new Set([...prevYears, ...newAvailableYears]);
          return Array.from(uniqueYears).sort((a, b) => b - a);
        });
        setNewsGroupByYearData((prevData) => {
          const updatedData = { ...prevData };
          for (const year in newNewsGroupByData) {
            if (updatedData[year]) {
              updatedData[year] = [...updatedData[year], ...newNewsGroupByData[year]];
            } else {
              updatedData[year] = newNewsGroupByData[year];
            }
          }
          // Calculate total loaded articles after merging
          const totalLoadedArticles = Object.values(updatedData).reduce((sum, arr) => sum + arr.length, 0);
          setHasMore(totalLoadedArticles < totalNewsCount);
          return updatedData;
        });
      } else {
        // No more data returned from API
        setHasMore(false);
      }

      setPageIndex((prevIndex) => prevIndex + 1);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching next page blog data:", error);
      setLoading(false);
      setHasMore(false);
    }
  };

  const handleScroll = () => {
    if (ticking.current) return;
    ticking.current = true;

    window.requestAnimationFrame(() => {
      let closestYear = selectedYear;
      let closestOffset = Infinity;

      Object.entries(yearRefs.current).forEach(([yearStr, el]) => {
        if (el) {
          const rect = el.getBoundingClientRect();
          const offset = Math.abs(rect.top);
          if (offset < closestOffset) {
            closestOffset = offset;
            closestYear = parseInt(yearStr);
          }
        }
      });

      if (closestYear !== selectedYear) {
        setSelectedYear(closestYear);
      }

      ticking.current = false;
    });
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [selectedYear]);

  const handleOnChipClick = async (targetYear: number) => {
    if (availableYearsArray.includes(targetYear)) {
      scrollToYear(targetYear);
      return;
    }

    setLoading(true);
    let found = false;
    let tempPageIndex = pageIndex + 1;
    const newYears: number[] = [];

    while (!found && hasMore) {
      try {
        const response = await apiservice.getNewsData({
          pageIndex: tempPageIndex,
          pageSize: 12,
        });
        const newNewsData = response?.rows;
        const newNewsGroupByData = newNewsData.reduce((acc: any, obj: ArticleType) => {
          const year = obj.year;
          if (typeof year !== "undefined" && year !== null) {
            if (!acc[year]) {
              acc[year] = [];
            }
            acc[year].push(obj);
          }
          return acc;
        }, {});
        const newAvailableYears = Object.keys(newNewsGroupByData)
          .map((key) => parseInt(key))
          .sort((a, b) => b - a);
        setAvailableYearsArray((prevYears) => {
          const uniqueYears = new Set([...prevYears, ...newAvailableYears]);
          return Array.from(uniqueYears).sort((a, b) => b - a);
        });
        setNewsGroupByYearData((prevData) => {
          const updatedData = { ...prevData };
          for (const year in newNewsGroupByData) {
            if (updatedData[year]) {
              updatedData[year] = [...updatedData[year], ...newNewsGroupByData[year]];
            } else {
              updatedData[year] = newNewsGroupByData[year];
            }
          }
          return updatedData;
        });
        if (!newNewsData || newNewsData.length === 0) {
          setHasMore(false);
          break;
        }

        if (newAvailableYears.includes(targetYear)) {
          found = true;
        }

        tempPageIndex += 1;
      } catch (error) {
        console.error("Error loading more years:", error);
        break;
      }
    }

    if (found) {
      scrollToYear(targetYear);
      setPageIndex(tempPageIndex - 1);
    }

    setLoading(false);
  };

  const scrollToYear = (year: number) => {
    const index = years.indexOf(year);
    if (virtuoso.current && index !== -1) {
      virtuoso.current.scrollToIndex({
        index,
        behavior: "smooth",
        offset: -140,
      });
    }
    // setSelectedYear(year);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    setNewsGroupByYearData(newsGroupByData);
    setPageIndex(1);
  }, [newsGroupByData]);

  useEffect(() => {
    setAvailableYearsArray(availableYears);
    setPageIndex(1);
    setHasMore(true);
  }, [availableYears]);

  const FilterChip = () => (
    <Chip
      chipData={years}
      selectedValue={selectedYear}
      onClick={(value) => {
        handleOnChipClick(Number(value));
      }}
    />
  );

  return (
    <div className="w-full min-h-screen flex flex-col justify-start items-center gap-4 mb-5">
      {years.length > 0 && (
        <div className={showTopButton ? "filter-chip-animation" : "filter-chip-non-animation "}>
          <FilterChip />
        </div>
      )}

      <ClientOnly
        fallback={availableYearsArray.map((y) => (
          <div key={y} className="w-full relative px-3 pb-5">
            <div className="h-full w-[3px] absolute left-2 top-4 bg-eventcardGradient opacity-100" />
            <div className="size-[10px] rounded-full absolute top-4 left-1 bg-textColor" />
            <div className="w-full flex flex-col justify-start items-start gap-2">
              <h3 className="text-textColor text-[25px] font-abcArizonaFlareVariable">{y}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5 px-3 w-full">
                {Array.isArray(newsGroupByYearData[y]) &&
                  newsGroupByYearData[y].map((data) => <ArticleComponent article={data} author={author} from={`${data.type}/${data.url_slug}`} />)}
              </div>
            </div>
          </div>
        ))}
      >
        <Virtuoso
          ref={virtuoso}
          style={{ height: "100%", width: "100%" }}
          data={availableYearsArray}
          endReached={handleFetchNextPageBlogData}
          increaseViewportBy={200}
          useWindowScroll
          itemContent={(i, date) => {
            return (
              <div key={i} className="w-full relative px-3 pb-5" ref={(el) => (yearRefs.current[date] = el)}>
                <div className="h-full w-[3px] absolute left-2 top-4 bg-eventcardGradient opacity-100" />
                <div className="size-[10px] rounded-full absolute top-2 left-1 bg-textColor" />
                <div className="w-full flex flex-col justify-start items-start gap-2">
                  <h3 className="text-textColor text-[20px] font-abcArizonaFlareVariable font-medium ml-3">{date}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5 px-3 w-full">
                    {Array.isArray(newsGroupByYearData[date]) &&
                      newsGroupByYearData[date].map((data, index) => (
                        <React.Fragment key={index}>
                          <MemoizedArticleComponent data={data} author={author} />
                        </React.Fragment>
                      ))}
                  </div>
                </div>
              </div>
            );
          }}
          components={{
            Footer: () => {
              return loading || hasMore ? (
                <LoadingSpinner />
              ) : (
                <NoHasMoreText className="text-textSecondaryColor" text={t("common.seeAllData", { data: "news" })} />
              );
            },
          }}
        />
      </ClientOnly>
    </div>
  );
}

const MemoizedArticleComponent = React.memo(({ data, author }: { data: ArticleType; author: categoryAndAutherProps[] }) => {
  return <ArticleComponent article={data} author={author} from={`${data.type}/${data.url_slug}`} />;
});
MemoizedArticleComponent.displayName = "MemoizedArticleComponent";

export default News;

export const ErrorBoundary = () => {
  return (
    <div className="w-full flex justify-center items-center min-h-[50vh]">
      <NodataFound text={"No data found"} />
    </div>
  );
};
